import { type RefObject, useLayoutEffect } from "react";

/**
 * 스크롤 영역 안 표들의 열 머리글 행(<tr data-header-row>)을 고정하기 위한 표시를 단다 — 스프레드시트의 틀 고정.
 *
 * 고정 자체는 브라우저 sticky 가 한다(호출부 CSS: 머리글 셀 position sticky · top var(--sticky-top)). 스크롤과 같은 프레임에
 * 붙어 흔들리지 않는다. 여기서는
 * - 머리글이 여러 행일 때 각 행이 위 행들 높이만큼 아래에 붙도록 위치(--sticky-top)를 잰다 — 표 크기가 바뀔 때만
 * - 아래 끝이 머리글 맨 아래에 닿는 셀에 data-header-bottom 을 단다 (고정 표시 그림자 자리) — 마지막 행의 셀만이 아니라
 *   위 행에서 행 병합으로 내려온 셀(예: "과목")까지
 * - 표 위쪽이 스크롤 영역 위로 넘어가 머리글이 붙어 있는 동안 표에 data-header-stuck 을 단다 — 위가 가려졌다는 표시를 켜는 용도라
 *   위치와 달리 한 프레임 늦어도 흔들림이 없다
 * contentKey 가 바뀌면(보여줄 표가 바뀌면) 다시 잰다
 */
export const useStickyHeaderRows = (
  scrollerRef: RefObject<HTMLElement>,
  contentKey: unknown,
) => {
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const headerRowsOf = (table: HTMLTableElement) => [
      ...table.querySelectorAll<HTMLTableRowElement>(
        ":scope > tbody > tr[data-header-row]",
      ),
    ];
    const tables = [...scroller.querySelectorAll("table")].filter(
      (table) => headerRowsOf(table).length > 0,
    );
    if (tables.length === 0) return;

    const measure = () => {
      for (const table of tables) {
        const rows = headerRowsOf(table);
        let top = 0;
        for (const [index, row] of rows.entries()) {
          row.style.setProperty("--sticky-top", `${top}px`);
          top += row.offsetHeight;
          for (const cell of row.cells)
            cell.toggleAttribute(
              "data-header-bottom",
              index + cell.rowSpan >= rows.length,
            );
        }
      }
    };
    const markStuck = () => {
      const scrollerTop = scroller.getBoundingClientRect().top;
      for (const table of tables) {
        const rect = table.getBoundingClientRect();
        table.toggleAttribute(
          "data-header-stuck",
          rect.top < scrollerTop && rect.bottom > scrollerTop,
        );
      }
    };

    measure();
    markStuck();
    scroller.addEventListener("scroll", markStuck, { passive: true });
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    tables.forEach((table) => observer?.observe(table));
    return () => {
      scroller.removeEventListener("scroll", markStuck);
      observer?.disconnect();
    };
  }, [scrollerRef, contentKey]);
};
